import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { MapPin, Users, Wallet, CheckCircle2, AlertTriangle, Plus } from "lucide-react";
import { frDate } from "@/lib/constants";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-zinc-500" />
      <span className="text-zinc-500">{label}</span>
      <span className="ml-auto text-white">{value}</span>
    </div>
  );
}

export default function EventQuickSheet({ selected, onClose, onComplete, onCreateQuote }) {
  return (
    <Sheet open={!!selected} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="border-zinc-800 bg-zinc-950 text-white sm:max-w-md">
        {selected && (
          <>
            <SheetHeader><SheetTitle className="text-white">{selected.title}</SheetTitle></SheetHeader>
            <div className="mt-6 space-y-4">
              <StatusBadge status={selected.status} />
              {selected.status !== "realise" && selected.status !== "annule" && selected.status !== "refuse" && (
                <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
                  {selected.pending_returns > 0 && (
                    <div className="mb-2 flex items-start gap-2 text-sm text-[#FFD500]">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>{selected.pending_returns} article(s) à rendre — validez l'état des lieux dans Stock & Matériel avant de clôturer.</span>
                    </div>
                  )}
                  <Button data-testid="complete-event" onClick={onComplete} className="w-full rounded-full bg-blue-600 font-semibold text-white hover:bg-blue-500">
                    <CheckCircle2 className="mr-1 h-4 w-4" />Marquer comme réalisé
                  </Button>
                </div>
              )}
              <div className="space-y-3 text-sm">
                <Row icon={MapPin} label="Date" value={frDate(selected.date)} />
                <Row icon={Users} label="Invités" value={selected.guests || "—"} />
                <Row icon={Wallet} label="Budget" value={selected.budget ? `${selected.budget} €` : "—"} />
                {selected.contact && <Row icon={Users} label="Contact" value={selected.contact.name} />}
              </div>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
                <p className="text-xs text-zinc-500">Catégorie</p>
                <p className="text-white">{selected.category}</p>
              </div>
              {selected.notes && <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-3"><p className="text-xs text-zinc-500">Notes internes</p><p className="whitespace-pre-wrap text-sm text-zinc-300">{selected.notes}</p></div>}
              {selected.reservations?.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-white">Matériel réservé</p>
                  <div className="space-y-1.5">
                    {selected.reservations.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-lg border border-zinc-800 p-2 text-sm">
                        <span className="text-zinc-300">{r.item_name} × {r.quantity}</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs ${r.status === "charge" ? "bg-blue-500/10 text-blue-400" : r.status === "retourne" ? "bg-emerald-500/10 text-emerald-400" : "bg-[#FF6A00]/10 text-[#FF6A00]"}`}>
                          {r.status === "charge" ? "Chargé" : r.status === "retourne" ? "Rendu" : "Réservé"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">Devis liés</p>
                  <Button data-testid="event-create-quote" onClick={() => onCreateQuote(selected.id)} className="h-8 rounded-full bg-[#FF6A00] px-3 text-xs font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-3 w-3" />Créer un devis</Button>
                </div>
                {selected.quotes.length === 0 ? <p className="text-sm text-zinc-500">Aucun devis rattaché.</p> :
                  selected.quotes.map((q) => <div key={q.id} className="flex items-center justify-between rounded-lg border border-zinc-800 p-2 text-sm"><span>{q.number}</span><StatusBadge status={q.status} type="quote" /></div>)}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
